import { useEffect, useMemo, useRef, useState } from 'react';
import { SingleSelectDropdown } from '../../components/common/SingleSelectDropdown';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { brandConfig } from '../../config/brands';
import { Label } from '../../components/common/Label';
import { logoCardBackground, pickPrimaryLogo } from '../../features/brands/logoAttributes';
import { CreateBrandModal } from '../../components/generation/scenario/CreateBrandModal';
import { useToast } from '../../hooks/useToast';
import { formatDate } from '../../utils/formatters';
import '../../components/common/CommonTable.css';
import '../../components/common/CommonFilters.css';
import './BrandListPage.css';

// Brand の正本は features/brands/types.ts
import type { Brand } from '../../features/brands/types';
import { ThreeDotsIcon } from '../../components/common/selectIcons';
import { CreateButton } from '../../components/common/CreateButton';

interface BrandRegistrySyncResult {
  accounts_processed: number;
  brands_seen_in_registry: number;
  brands_matched: number;
  brands_unmatched_in_registry: string[];
  errors: string[];
}

export default function BrandListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('true');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const headerMenuRef = useRef<HTMLDivElement>(null);

  // ヘッダーメニュー外クリックで閉じる
  useEffect(() => {
    if (!isHeaderMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target as Node)) {
        setIsHeaderMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isHeaderMenuOpen]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['brands', searchQuery, isActiveFilter],
    queryFn: async () => {
      const params: any = {};
      if (searchQuery) params.search = searchQuery;
      if (isActiveFilter !== 'all') params.is_active = isActiveFilter;

      const response = await apiClient.get('/brands', { params });
      return response.data;
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const brandRegistrySyncMutation = useMutation<BrandRegistrySyncResult>({
    mutationFn: async () => {
      const response = await apiClient.post<BrandRegistrySyncResult>(
        '/amazon-ads/brand-registry/sync'
      );
      return response.data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      // 診断的なトースト表示
      let msg: string;
      if (result.accounts_processed === 0) {
        msg = 'Amazon Ads アカウントが接続されていません';
      } else if (result.brands_seen_in_registry === 0) {
        msg = 'Amazon Brand Registry に登録されたブランドが見つかりませんでした';
      } else if (result.brands_matched > 0) {
        msg = `${result.brands_matched} 件のブランドを Amazon Brand Registry と紐づけました (Registry 側: ${result.brands_seen_in_registry} 件)`;
      } else {
        const unmatched = result.brands_unmatched_in_registry.slice(0, 5).join('、');
        const more =
          result.brands_unmatched_in_registry.length > 5
            ? ` 他 ${result.brands_unmatched_in_registry.length - 5} 件`
            : '';
        msg = `Registry 側のブランド名と一致するローカルブランドがありませんでした。Registry 側: ${unmatched}${more}`;
      }
      toast.show(msg);
    },
    onError: (err: any) => {
      const detail =
        err?.response?.data?.error || '同期に失敗しました (Amazon Ads アカウント未接続の可能性)';
      toast.show(detail);
    },
  });

  // お気に入りブランド一覧
  const { data: favoritesData } = useQuery({
    queryKey: ['user', 'favorite-brands'],
    queryFn: async () => {
      const response = await apiClient.get('/user/favorite-brands');
      return response.data as { favorites: { brand_id: string | null; brand_name: string | null }[] };
    },
    staleTime: 60_000,
  });

  const favoriteBrandIds = useMemo(() => {
    const set = new Set<string>();
    for (const fav of favoritesData?.favorites || []) {
      if (fav.brand_id) set.add(fav.brand_id);
    }
    return set;
  }, [favoritesData]);

  // お気に入りトグル（楽観的更新）
  const toggleFavoriteMutation = useMutation({
    mutationFn: async (params: { brandId: string; isFavorite: boolean }) => {
      if (params.isFavorite) {
        const search = new URLSearchParams({ brand_id: params.brandId });
        await apiClient.delete(`/user/favorite-brands?${search.toString()}`);
      } else {
        await apiClient.post('/user/favorite-brands', { brand_id: params.brandId });
      }
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ['user', 'favorite-brands'] });
      const prev = queryClient.getQueryData<{ favorites: { brand_id: string | null; brand_name: string | null }[] }>(['user', 'favorite-brands']);
      const nextFavorites = params.isFavorite
        ? (prev?.favorites || []).filter((f) => f.brand_id !== params.brandId)
        : [...(prev?.favorites || []), { brand_id: params.brandId, brand_name: null }];
      queryClient.setQueryData(['user', 'favorite-brands'], { favorites: nextFavorites });
      return { prev };
    },
    onError: (_err, _params, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['user', 'favorite-brands'], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'favorite-brands'] });
    },
  });

  const brands: Brand[] = data?.brands || [];

  // お気に入りを上位に固める（同グループ内は元の順序を維持＝stable sort）
  const sortedBrands = useMemo(() => {
    return brands
      .map((brand, index) => ({ brand, isFavorite: favoriteBrandIds.has(brand.id), index }))
      .sort((a, b) => {
        if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
        return a.index - b.index;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, favoriteBrandIds]);

  const handleRowClick = (brandId: string) => {
    navigate(`/brands/${brandId}`);
  };

  // ロゴの優先順位 (is_default → 先頭) で表示するロゴを選ぶ。案件のブランド選択と共通 (logoAttributes.pickPrimaryLogo)
  const getPrimaryLogo = (logos?: Brand['logos']) => pickPrimaryLogo(logos);

  return (
    <div className="page admin-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/sign_badge_badge_1.svg" alt="" className="page-title-icon" />
            ブランド管理
          </h1>
          <p className="page-description">
            組織で取り扱うブランドを表示します
          </p>
        </div>
        <div className="page-header-right">
          <CreateButton onClick={() => setShowCreateModal(true)}>新規ブランド作成</CreateButton>
          {brandConfig.features.amazonIntegration && (
            <div className="brand-header-menu" ref={headerMenuRef}>
              <button
                type="button"
                className="brand-header-menu-trigger"
                onClick={() => setIsHeaderMenuOpen((v) => !v)}
                aria-label="その他のアクション"
                aria-haspopup="menu"
                aria-expanded={isHeaderMenuOpen}
              >
                <ThreeDotsIcon vertical />
              </button>
              {isHeaderMenuOpen && (
                <div className="brand-header-menu-dropdown" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="brand-header-menu-item"
                    onClick={() => {
                      setIsHeaderMenuOpen(false);
                      brandRegistrySyncMutation.mutate();
                    }}
                    disabled={brandRegistrySyncMutation.isPending}
                  >
                    {brandRegistrySyncMutation.isPending
                      ? '同期中...'
                      : 'Amazon Brand Registry を同期'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <section className="list-controls">
        {/* スマホ用トグルボタン */}
        <button
          type="button"
          className="filter-toggle-button"
          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
        >
          <img src="/filter_sort_lines_descending.svg" alt="" className="filter-toggle-icon" />
          <span>フィルタ・並び替え</span>
          <span className="filter-toggle-count">全 {brands.length} 件</span>
          <span className={`filter-toggle-chevron ${isFilterExpanded ? 'expanded' : ''}`} />
        </button>

        <div className={`controls-row ${isFilterExpanded ? 'expanded' : ''}`}>
          <div className="filter-section">
            <img src="/filter_sort_lines_descending.svg" alt="" className="filter-icon" />
            <span className="filter-label">フィルタ</span>
            <div className="search-box">
              <input
                type="text"
                placeholder="ブランド名で検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <label>ステータス</label>
              {/* ブランドのページのドロップダウンはサイト共通のカスタム (利用者指示 2026-10-30) */}
              <SingleSelectDropdown
                label="すべて"
                labelOnly
                fitContent
                defaultValue="all"
                value={isActiveFilter}
                onChange={setIsActiveFilter}
                options={[
                  { value: 'all', label: 'すべて' },
                  { value: 'true', label: 'アクティブ' },
                  { value: 'false', label: '非アクティブ' },
                ]}
              />
            </div>
            <div className="total-count">
              全 <strong>{brands.length}</strong> 件
            </div>
          </div>
          <div className="sort-section">
            <button onClick={() => refetch()} className="text-button" disabled={isFetching}>
              {isFetching ? (
                <>
                  <img src="/refresh.svg" alt="" style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
                  更新中...
                </>
              ) : (
                <>
                  <img src="/refresh.svg" alt="" style={{ width: '14px', height: '14px' }} />
                  更新
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="admin-alert admin-alert-error">
          エラーが発生しました: {error instanceof Error ? error.message : '不明なエラー'}
        </div>
      )}

      {isLoading ? (
        <div className="admin-loading">
          読み込み中...
        </div>
      ) : brands.length === 0 ? (
          <div className="admin-empty-state">
            <div className="admin-empty-state-title">ブランドが見つかりませんでした</div>
            <div className="admin-empty-state-description">
              Amazon商品を取得すると、自動的にブランドが作成されます。
            </div>
          </div>
        ) : (
          <div className="table-container">
            <table className="brands-table common-table">
              <thead>
                <tr>
                  <th className="col-logo">ロゴ</th>
                  <th className="col-brand-name">ブランド名</th>
                  <th className="col-status">ステータス</th>
                  <th className="col-product-count">商品数</th>
                  <th className="col-materials">素材</th>
                  <th className="col-logo-count">ロゴ数</th>
                  <th className="col-date">登録日</th>
                </tr>
              </thead>
              <tbody>
                {sortedBrands.map(({ brand, isFavorite }) => {
                  const primaryLogo = getPrimaryLogo(brand.logos);
                  return (
                    <tr
                      key={brand.id}
                      onClick={() => handleRowClick(brand.id)}
                      className="clickable-row"
                    >
                      <td className="col-logo">
                        {primaryLogo?.logos?.url ? (
                          <img
                            src={primaryLogo.logos.url}
                            alt={`${brand.brand_name} logo`}
                            className="product-thumbnail"
                            style={{ background: logoCardBackground(primaryLogo) }}
                          />
                        ) : (
                          <div className="no-image">NO IMAGE</div>
                        )}
                      </td>
                      <td className="col-brand-name">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span className="brand-name">{brand.brand_name}</span>
                          <button
                            type="button"
                            className="brand-favorite-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavoriteMutation.mutate({ brandId: brand.id, isFavorite });
                            }}
                            aria-label={isFavorite ? 'お気に入りから外す' : 'お気に入りに追加'}
                            title={isFavorite ? 'お気に入りから外す' : 'お気に入りに追加'}
                          >
                            <img
                              src={isFavorite ? '/star_1_gold.svg' : '/star_1.svg'}
                              alt=""
                              className={`brand-favorite-icon ${isFavorite ? '' : 'is-empty'}`}
                            />
                          </button>
                          {brand.amazon_brand_entity_id && (
                            <span
                              title="Amazon Brand Registry"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px',
                                borderRadius: '6px',
                                background: 'rgba(245, 241, 230, 0.08)',
                              }}
                            >
                              <img
                                src="/amazon_icon.svg"
                                alt="Amazon Brand Registry"
                                style={{ width: '16px', height: '16px' }}
                              />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="col-status">
                        {brand.is_active ? (
                          <Label variant="green">アクティブ</Label>
                        ) : (
                          <Label variant="red">非アクティブ</Label>
                        )}
                      </td>
                      <td className="col-product-count">
                        {(brand.product_count ?? 0) > 0 ? (
                          <Link
                            to={`/products/${brandConfig.features.amazonIntegration ? 'amazon' : 'manual'}?brand_id=${brand.id}`}
                            className="count-pill"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(brand.product_count ?? 0).toLocaleString()}
                          </Link>
                        ) : (
                          <span className="count-pill count-pill--zero">0</span>
                        )}
                      </td>
                      <td className="col-materials">
                        <div className="brand-materials-links">
                          <Link
                            to={`/materials/shared?brand_id=${brand.id}`}
                            className="brand-material-link"
                            title="共有フォルダの素材を表示"
                            aria-label="共有フォルダの素材を表示"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <img src="/cloud_file.svg" alt="" />
                          </Link>
                          <Link
                            to={`/materials/personal?brand_id=${brand.id}`}
                            className="brand-material-link brand-material-link--personal"
                            title="個人フォルダの素材を表示"
                            aria-label="個人フォルダの素材を表示"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <img src="/single_neutral_folder_box.svg" alt="" />
                          </Link>
                        </div>
                      </td>
                      <td className="col-logo-count">
                        {brand.logos && brand.logos.length > 0 ? (
                          <span>{brand.logos.length}</span>
                        ) : (
                          <span style={{ color: 'rgba(245, 241, 230, 0.4)' }}>-</span>
                        )}
                      </td>
                      <td className="col-date">{formatDate(brand.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      <CreateBrandModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onBrandCreated={(brandId) => navigate(`/brands/${brandId}`)}
      />
    </div>
  );
}
